/**
 * Billing & Plan — the org-admin BIMGO-credit wallet. A port of
 * `bimgo-ai-web`'s `WalletView` (#64), the third Settings tab 2.5 never had.
 *
 * Five cards, each fetching its own data so one slow or refused call does not
 * blank the others: the subscription, the balance, the plan (with the org's
 * monthly cap and the button that edits it), per-member spend limits, and the
 * purchases ledger. The same five the reference renders, in the same order.
 *
 * **Per-member credit consumption is deliberately absent** (owner, 2026-09-03).
 * An older `bimgo-ai-web` had a "Consumption by member" table here with a
 * per-request drill-down behind each row; the current one does not, and the
 * desktop does not want it either. Who spent what is the members table's job on
 * the usage dashboard. api-v2 still serves the two endpoints — this is a
 * decision about the screen, not about the data.
 *
 * **The subscription card is the reason this shipped.** #63 blocks an
 * organisation whose subscription has lapsed; until this tab existed there was
 * nowhere in the desktop that said *when* it lapses, so the first a user knew of
 * it was the wall. `plan_name`, `seat_count`, `status` and `ends_at` have been on
 * `lib/organizations.ts` all along with nothing reading them.
 *
 * Everything is in BIMGO credits — there is no money concept on the client.
 *
 * Two rules that run through every card here, both ways of lying to an admin
 * about their own account:
 *
 *   - **A null limit is unlimited, never zero.** `lib/tokens.ts` keeps them
 *     apart on the wire; the cards must not flatten them on screen.
 *   - **An `org_shared` plan has no per-member allowance.** Its
 *     `max_tokens_per_month` is a per-member pool-draw ceiling, so showing it
 *     as an org-wide limit would name a limit that does not exist.
 *     `max_requests_per_month` is not enforced on any plan (bimgo-ai-api-v3#32).
 */
import { useEffect, useRef, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { API_BASE_URL, isConfigured } from "../../lib/config";
import type { Organization } from "../../lib/organizations";
import { statusOf } from "../../lib/threads";
import { getOrgUsage, type OrgUsageSummary } from "../../lib/usage";
import {
  formatCredits,
  getOrgTokenOverview,
  getOrgTokenPurchases,
  getOrganizationLimits,
  subscriptionProgress,
  type OrganizationLimits,
  type TokenOverview,
  type TokenPurchaseItem,
} from "../../lib/tokens";
import { LoadingBlock } from "../settings/Spinner";
import { MemberLimitsCard, OrgLimitEditButton } from "./SpendLimitsCards";
import {
  BadgeCheckIcon,
  EmptyRow,
  ErrorRow,
  Field,
  HistoryIcon,
  LayersIcon,
  WalletIcon,
  useDateFormat,
  useNumberLocale,
} from "./shared";

export function WalletView({
  organization,
  orgId,
  token,
  canManageLimits,
}: {
  /** The org record the shell already fetched — the subscription is on it. */
  organization: Organization | null;
  orgId: string;
  token: string | null;
  canManageLimits: boolean;
}) {
  return (
    <>
      <ActiveSubscriptionCard organization={organization} />
      <WalletBalanceCard orgId={orgId} token={token} />
      <PlanCard orgId={orgId} token={token} organization={organization} canEdit={canManageLimits} />
      <MemberLimitsCard orgId={orgId} token={token} canEdit={canManageLimits} />
      <WalletPurchasesCard orgId={orgId} token={token} />
    </>
  );
}

// -- subscription ------------------------------------------------------------

/**
 * Plan name, status, and the window it runs in — read from the org record the
 * shell already holds, so this card costs no request.
 */
function ActiveSubscriptionCard({ organization }: { organization: Organization | null }) {
  const { t } = useLocale();
  const formatDate = useDateFormat();
  const sub = organization?.active_subscription ?? null;

  return (
    <div className="settings-card">
      <div className="card-head-row">
        <h3 className="card-title">
          <span className="card-title-icon">
            <BadgeCheckIcon />
          </span>
          {t("wallet.subscription.title")}
        </h3>
        {/* api-v2 nulls the whole slot for an expired, disabled or absent
            subscription, so a present one is an active one — the same fact the
            shell's gate reads (`lib/orgGate.ts`). */}
        {sub ? <span className="wallet-badge">{t("wallet.subscription.active")}</span> : null}
      </div>

      {!sub ? (
        <EmptyRow message={t("wallet.subscription.none")} />
      ) : (
        <>
          <div className="wallet-grid">
            {/* `bimgo-ai-web` hides the plan name — plans are named for internal
                bookkeeping ("pca - paid [a881f06d]"). #64 asks for it anyway,
                and this is the right card for it: an admin on the phone to
                support needs the name their subscription is filed under, and
                this is the only screen in the desktop that has it. */}
            <Field label={t("wallet.subscription.plan")} value={sub.plan_name} />
            {/* Seats are a Plan field and are shown there — the reference moved
                them out of this card for that reason, and stating one number in
                two places is how the two come to disagree. */}
            <Field
              label={t("wallet.subscription.renewsOn")}
              value={formatDate(sub.ends_at, sub.timezone)}
            />
          </div>
          <SubscriptionPeriod sub={sub} />
        </>
      )}
    </div>
  );
}

function SubscriptionPeriod({
  sub,
}: {
  sub: NonNullable<Organization["active_subscription"]>;
}) {
  const { t } = useLocale();
  const formatDate = useDateFormat();
  const { percent, daysLeft } = subscriptionProgress(sub.starts_at, sub.ends_at);

  return (
    <div className="sub-period">
      <div className="sub-period-head">
        <span className="quota-label">{t("wallet.subscription.period")}</span>
        <span className="sub-period-left">
          {t("wallet.subscription.daysLeft", { count: daysLeft.toLocaleString() })}
        </span>
      </div>
      <div
        className="quota-bar"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t("wallet.subscription.period")}
      >
        <span style={{ width: `${percent}%` }} />
      </div>
      <div className="sub-period-dates">
        <span>{formatDate(sub.starts_at, sub.timezone)}</span>
        <span>{formatDate(sub.ends_at, sub.timezone)}</span>
      </div>
    </div>
  );
}

// -- balance -----------------------------------------------------------------

function WalletBalanceCard({ orgId, token }: { orgId: string; token: string | null }) {
  const { t } = useLocale();
  const numberLocale = useNumberLocale();
  const formatDate = useDateFormat();
  const [state, setState] = useState<Load<TokenOverview>>({ kind: "loading" });

  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (!orgId || !isConfigured()) return;
    let cancelled = false;
    setState({ kind: "loading" });
    getOrgTokenOverview(tokenRef.current, orgId, { apiBaseUrl: API_BASE_URL() })
      .then((value) => !cancelled && setState({ kind: "loaded", value }))
      .catch((e: unknown) => !cancelled && setState(failed(e)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  return (
    <div className="settings-card">
      <h3 className="card-title">
        <span className="card-title-icon">
          <WalletIcon />
        </span>
        {t("wallet.balance.title")}
      </h3>
      <Loaded state={state}>
        {(overview) => (
          <>
            {/* No label above the figure — the card's heading already names it. */}
            <div className="wallet-balance">{formatCredits(overview.wallet_balance, numberLocale)}</div>
            {/* Only when something is actually about to expire: a permanent
                "0 credits expiring soon" row is noise that trains the reader to
                skip the line that matters. */}
            {overview.expiring_soon_credits > 0 ? (
              <p className="wallet-warn" role="status">
                {t("wallet.balance.expiringSoon", {
                  credits: formatCredits(overview.expiring_soon_credits, numberLocale),
                })}
                {overview.next_expiry_at
                  ? ` ${t("wallet.balance.nextExpiry", { date: formatDate(overview.next_expiry_at) })}`
                  : ""}
              </p>
            ) : null}
          </>
        )}
      </Loaded>
    </div>
  );
}

// -- plan --------------------------------------------------------------------

/**
 * The plan behind the subscription, and the org's monthly cap.
 *
 * Two reads, because the card needs `billing_mode` from the limits response to
 * decide which of the plan's fields describe this org at all. A failed limits
 * lookup leaves the card on its per-seat reading rather than blanking figures
 * that did load.
 */
function PlanCard({
  orgId,
  token,
  organization,
  canEdit,
}: {
  orgId: string;
  token: string | null;
  organization: Organization | null;
  canEdit: boolean;
}) {
  const { t } = useLocale();
  const numberLocale = useNumberLocale();
  const formatDate = useDateFormat();
  const [state, setState] = useState<Load<OrgUsageSummary>>({ kind: "loading" });
  const [limits, setLimits] = useState<OrganizationLimits | null>(null);

  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (!orgId || !isConfigured()) return;
    let cancelled = false;
    setState({ kind: "loading" });
    setLimits(null);
    getOrgUsage(tokenRef.current, orgId, { apiBaseUrl: API_BASE_URL() })
      .then((value) => !cancelled && setState({ kind: "loaded", value }))
      .catch((e: unknown) => !cancelled && setState(failed(e)));
    getOrganizationLimits(tokenRef.current, orgId, { apiBaseUrl: API_BASE_URL() })
      .then((value) => !cancelled && setLimits(value))
      // Deliberately swallowed — see the card's header.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const seatCount = organization?.active_subscription?.seat_count ?? null;
  const isOrgShared = limits?.billing_mode === "org_shared";
  const orgCap = limits?.monthly_credit_limit ?? null;

  return (
    <div className="settings-card">
      <div className="card-head-row">
        <h3 className="card-title">
          <span className="card-title-icon">
            <LayersIcon />
          </span>
          {t("wallet.plan.title")}
        </h3>
        {/* The org cap is enforced on either plan and is one of the fields
            directly below, so the button edits the figure being looked at. */}
        <OrgLimitEditButton
          orgId={orgId}
          token={token}
          limits={limits}
          canEdit={canEdit}
          onChange={setLimits}
        />
      </div>

      <Loaded state={state}>
        {(summary) =>
          !summary.plan ? (
            <EmptyRow message={t("wallet.plan.empty")} />
          ) : (
            <div className="wallet-grid">
              {/* The plan's *name* is deliberately absent: plans are named for
                  internal bookkeeping ("pca - paid [a881f06d]"). The
                  subscription card above shows the name a customer knows. */}
              <Field
                label={t("limits.plan.mode")}
                value={
                  limits === null
                    ? "—"
                    : isOrgShared
                      ? t("limits.mode.org_shared")
                      : t("limits.mode.per_seat")
                }
              />
              <Field
                label={t("limits.plan.seats")}
                value={seatCount === null ? "—" : seatCount.toLocaleString(numberLocale)}
              />
              {/* Per-seat only: the prepaid monthly allowance each member spends
                  before any wallet draw. org_shared has no allowance, so the
                  field would state a number that governs nothing. */}
              {!isOrgShared ? (
                <Field
                  label={t("wallet.plan.maxCredits")}
                  value={
                    summary.plan.max_tokens_per_month === null
                      ? t("limits.unlimited")
                      : formatCredits(summary.plan.max_tokens_per_month, numberLocale)
                  }
                />
              ) : null}
              {/* Both modes: the ceiling on wallet spend for the whole org. It
                  is enforced on a per-seat plan too — the allowance is prepaid
                  and does not count against it — so hiding it there left admins
                  with an invisible limit they could hit but not raise. Shown
                  beside the allowance rather than instead of it: both are real
                  at once and they measure different things. */}
              <Field
                label={t("limits.org.title")}
                value={
                  orgCap === null ? (
                    t("limits.unlimited")
                  ) : (
                    <>
                      {formatCredits(orgCap, numberLocale)}
                      {limits?.monthly_credit_limit_is_default ? (
                        <span className="wallet-badge is-muted">{t("limits.org.default")}</span>
                      ) : null}
                    </>
                  )
                }
              />
              <Field
                label={t("wallet.plan.cycleReset")}
                value={formatDate(
                  summary.usage_cycle?.monthly_end ?? null,
                  summary.plan.timezone,
                )}
              />
            </div>
          )
        }
      </Loaded>
    </div>
  );
}

// -- purchases ---------------------------------------------------------------

function WalletPurchasesCard({ orgId, token }: { orgId: string; token: string | null }) {
  const { t } = useLocale();
  const numberLocale = useNumberLocale();
  const formatDate = useDateFormat();
  const [state, setState] = useState<Load<TokenPurchaseItem[]>>({ kind: "loading" });

  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (!orgId || !isConfigured()) return;
    let cancelled = false;
    setState({ kind: "loading" });
    getOrgTokenPurchases(tokenRef.current, orgId, {}, { apiBaseUrl: API_BASE_URL() })
      .then((value) => !cancelled && setState({ kind: "loaded", value }))
      .catch((e: unknown) => !cancelled && setState(failed(e)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  return (
    <div className="settings-card">
      <h3 className="card-title">
        <span className="card-title-icon">
          <HistoryIcon />
        </span>
        {t("wallet.purchases.title")}
      </h3>
      <Loaded state={state}>
        {(rows) =>
          rows.length === 0 ? (
            <EmptyRow message={t("wallet.purchases.empty")} />
          ) : (
            <div className="members-scroll">
              <table className="members-table">
                <thead>
                  <tr>
                    <th className="col-text">{t("wallet.purchases.colDate")}</th>
                    <th className="col-text">{t("wallet.purchases.colType")}</th>
                    <th>{t("wallet.purchases.colCredits")}</th>
                    <th>{t("wallet.purchases.colBalance")}</th>
                    <th className="col-text">{t("wallet.purchases.colExpires")}</th>
                    <th className="col-text">{t("wallet.purchases.colReference")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.occurred_at}-${i}`}>
                      <td className="col-text">{formatDate(r.occurred_at)}</td>
                      <td className="col-text">
                        <span className="wallet-badge is-muted">{r.type}</span>
                      </td>
                      <td className="num">
                        {/* An adjustment or refund is negative and already
                            carries its sign; only an addition gets a "+". */}
                        {r.bimgo_credit > 0 ? "+" : ""}
                        {formatCredits(r.bimgo_credit, numberLocale)}
                      </td>
                      <td className="num">{formatCredits(r.balance_after, numberLocale)}</td>
                      <td className="col-text is-soft">
                        {r.expires_at ? formatDate(r.expires_at) : "—"}
                      </td>
                      <td className="col-text is-soft">{r.reference ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </Loaded>
    </div>
  );
}

// -- the three states every card has -----------------------------------------

type Load<T> =
  | { kind: "loading" }
  | { kind: "forbidden" }
  | { kind: "error" }
  | { kind: "loaded"; value: T };

/**
 * A 403 is **not** a failure: it means the caller is not an org admin, and the
 * session behind it is working. Reporting it as "could not load" would send an
 * admin looking for an outage that is not there. Same rule as the dashboard's.
 */
function failed(error: unknown): Load<never> {
  return statusOf(error) === 403 ? { kind: "forbidden" } : { kind: "error" };
}

function Loaded<T>({
  state,
  children,
}: {
  state: Load<T>;
  children: (value: T) => React.ReactNode;
}) {
  const { t } = useLocale();
  if (state.kind === "loading") return <LoadingBlock size={24} />;
  if (state.kind === "forbidden") return <ErrorRow message={t("error.forbidden")} />;
  if (state.kind === "error") return <ErrorRow message={t("wallet.loadFailed")} />;
  return <>{children(state.value)}</>;
}

export default WalletView;
